import { Component, OnInit, Input, Output, EventEmitter, HostListener, ViewChild, OnDestroy, inject } from '@angular/core';
import { FaceCheckService } from './face-check.service';
import { BrowserCompatibilityService } from '@shared/services/browser-compatibility';
import { MediaDevicesService } from '@shared/services/media-devices';
import { DialogService, waitForDialogResult } from '@shared/services/dialog';
import { VideoComponent } from './components/video/video.component';
import { DialogIcon } from '@shared/components/dialog';
import { CheckComponent } from './components/pages/check/check.component';
import { LoadingStatus } from '@shared/models/loading-status.enum';
import { I18nService } from '@shared/i18n';
import { FaceCheckPage } from './face-check-page.enum';

const PREFIX = '[FaceCheckComponent]';

@Component({
  selector: 'app-face',
  templateUrl: './face-check.component.html',
  styleUrls: ['./face-check.component.scss'],
  standalone: false,
})
export class FaceCheckComponent implements OnInit, OnDestroy {
  protected readonly i18n = inject(I18nService);
  private readonly faceCheckService = inject(FaceCheckService);
  private readonly mediaDevices = inject(MediaDevicesService);
  private readonly dialog = inject(DialogService);
  readonly browserCompatibility = inject(BrowserCompatibilityService);

  public readonly Page = FaceCheckPage;
  public readonly LoadingStatus = LoadingStatus;

  @Input() progress: number;
  @Input() progressMessage: string;
  @Input() showBrowserProblemLink: boolean = false;
  @Output() fileReady = new EventEmitter<Blob>();
  @ViewChild('video', { static: true }) video: VideoComponent;
  @ViewChild('checkPage', { static: false }) checkPage: CheckComponent | undefined;

  public page = FaceCheckPage.Media;
  public alive = true;
  public loadingStatus = LoadingStatus.Idle;
  public webcamStatus = LoadingStatus.Idle;
  public stream: MediaStream | undefined;
  private currentDevice: MediaDeviceInfo | undefined;

  private readonly IS_DESKTOP: boolean;

  public get showVideo() {
    return this.page === FaceCheckPage.Check && this.loadingStatus === LoadingStatus.Success;
  }

  constructor() {
    const browserCompatibility = this.browserCompatibility;

    this.IS_DESKTOP = browserCompatibility.isDesktop();
  }

  ngOnInit() {
    this.load();
  }

  private async load() {
    this.loadingStatus = LoadingStatus.Pending;
    this.initFaceCheckPage();
    console.debug(PREFIX, 'Check: Starting on', this.page);
    this.detectScreenOrientation();
    if (this.page === FaceCheckPage.Check) {
      await this.obtainVideo();
    }
    this.loadingStatus = LoadingStatus.Success;
  }

  ngOnDestroy(): void {
    this.alive = false;
    this.stopStream();
  }

  @HostListener('window:resize')
  onResize(): void {
    console.debug(PREFIX, 'Resize detected');
    this.detectScreenOrientation();
  }

  private moveToPage(page: FaceCheckPage) {
    if (this.page === page) {
      return;
    }
    console.debug(PREFIX, `Moving to page "${page}" from "${this.page}"`);
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: 'instant',
    });
    this.page = page;
  }

  private initFaceCheckPage() {
    if (this.faceCheckService.hasAlreadyConsented()) {
      this.moveToPage(FaceCheckPage.Check);
    } else {
      this.moveToPage(FaceCheckPage.Media);
    }
  }

  private detectScreenOrientation() {
    if (window.innerWidth >= window.innerHeight && !this.IS_DESKTOP) {
      this.moveToPage(FaceCheckPage.OrientationError);
    } else {
      this.initFaceCheckPage();
    }
  }

  public async handleConfirmMedia() {
    console.debug(PREFIX, 'Confirm media was called.');
    await this.obtainVideo();
    console.debug(PREFIX, 'Moving to check page');
    this.moveToPage(FaceCheckPage.Check);
  }

  /**
   * Handle the check page's request to change media.
   *
   * This will happen when the user hits the button to change camera device.
   */
  public async handleChangeMedia() {
    console.debug(PREFIX, 'A request to change media was called.');
    await this.changeMedia();
  }

  /**
   * Handle the check page's request for a video.
   *
   * This will only happen if we're on the check page and, somehow, there is no video.
   */
  public async handleRequestVideo() {
    console.debug(PREFIX, 'Check page has requested video.');
    await this.obtainVideo();
    console.debug(PREFIX, 'Telling check page we have video now.');
    this.checkPage?.videoObtained();
  }

  private async obtainVideo() {
    const FN_PREFIX = '[obtainVideo]';
    console.debug(PREFIX, FN_PREFIX, '(0/2)', 'Beginning to obtain video stream');
    this.webcamStatus = LoadingStatus.Pending;
    this.faceCheckService.rememberConsent();
    let stream: MediaStream;

    try {
      stream = await this.mediaDevices.getVideoStream();
      this.webcamStatus = LoadingStatus.Success;
      console.debug(PREFIX, FN_PREFIX, '(1/2)', 'Obtained video stream');
    } catch {
      this.webcamStatus = LoadingStatus.Failed;
      this.onMediaError();
      console.debug(PREFIX, FN_PREFIX, 'Failed to obtain video stream');
      return;
    }

    if (this.alive) {
      this.stream = stream;
      await this.video.playVideo(stream);
      console.debug(PREFIX, FN_PREFIX, '(2/2)', 'Video stream is playing');
    } else {
      console.debug(PREFIX, FN_PREFIX, 'Component is dead, discarding newly obtained stream');
      this.mediaDevices.stopStream(stream);
    }
  }

  public onMediaError() {
    this.moveToPage(FaceCheckPage.MediaError);
  }

  public handleFileReady(video: Blob): void {
    console.debug(PREFIX, 'Moving to upload');
    this.moveToPage(FaceCheckPage.Upload);
    this.fileReady.emit(video);
  }

  /**
   * Trigger the loading of a different video device, chosen by the user.
   */
  private async changeMedia() {
    const FN_PREFIX = '[changeMedia]';
    console.debug(PREFIX, FN_PREFIX, 'Opening dialog to change video stream');
    const devices: MediaDeviceInfo[] = await this.mediaDevices.getVideoDeviceList();

    const dialogRef = this.dialog.openSelectDialog({
      data: {
        title: this.i18n.getPhrase('register.face.camera.title'),
        content: [this.i18n.getPhrase('register.face.camera.content')],
        options: devices.map((device) => ({ value: device.deviceId, label: device.label })),
        initialValue: this.currentDevice?.deviceId,
        cancel: this.i18n.getPhrase('register.verify_face.opt_out_dialog.cancel'),
        confirm: this.i18n.getPhrase('register.face.camera.save'),
      },
    });

    const result = await waitForDialogResult(dialogRef);

    if (!result.confirmed || !result.value || result.value === this.currentDevice?.deviceId) {
      console.debug('No device chosen, or current device was chosen.');
      return;
    }

    console.debug(PREFIX, FN_PREFIX, '(0/3)', 'Beginning to obtain video stream');
    this.currentDevice = devices.find((device) => device.deviceId === result.value);

    let stream: MediaStream;
    try {
      console.debug(PREFIX, FN_PREFIX, '(1/3) Stopping old stream before obtaining new stream');
      this.stopStream();
      stream = await this.mediaDevices.getSpecificVideoStream(result.value);
      console.debug(PREFIX, FN_PREFIX, '(2/3)', 'Obtained new video stream');
    } catch {
      console.debug(PREFIX, FN_PREFIX, 'Failed to obtain video stream');
      this.dialog.openConfirmDialog({
        data: {
          icon: DialogIcon.InfoRed,
          title: this.i18n.getPhrase('register.face.camera.error.title'),
          content: this.i18n.getPhrase('register.face.camera.error.content'),
          primary: this.i18n.getPhrase('register.link.failed_dialog.primary_button'),
        },
      });
      return;
    }

    if (this.alive) {
      this.stream = stream;
      this.webcamStatus = LoadingStatus.Success;
      await this.video.playVideo(stream);
      console.debug(PREFIX, FN_PREFIX, '(3/3)', 'Video stream is playing');
    } else {
      console.debug(PREFIX, FN_PREFIX, 'Component is dead, discarding newly obtained stream');
      this.mediaDevices.stopStream(stream);
    }
  }

  private stopStream() {
    if (this.stream) {
      console.debug(PREFIX, '[stopStream]', 'Stopping current stream');
      this.mediaDevices.stopStream(this.stream);
    }
  }
}
